import * as React                                    from 'react';
import { SubmissionError, formValueSelector, reset } from 'redux-form';
import { useTranslation }                            from 'react-i18next';
import { connect }                                   from 'react-redux';

import { hasAccess } from '../../../common/services/accessChecker';

import { useSidebar }   from '../../providers/sidebar';
import sidebarConstants from '../../providers/sidebar/sidebar.constants';
import { useTab }       from '../../providers/tabs';
import { useUser }      from '../../providers/user';
import { useWhereBuy }  from '../../providers/whereBuy';

import RenderButton        from '../buttons/base/RenderButton';
import { RenderBasicTabs } from '../tabs/RenderBasicTabs';

import BaseSidebar        from './base/BaseSidebar';
import AuthForm           from './components/AuthForm';
import ChangePasswordForm from './components/ChangePasswordForm';
import RegisterForm       from './components/RegisterForm';

const ProfileSidebar = function(props) {
  const { t } = useTranslation();

  const {
          dispatch,
          emailForm,
        } = props;

  const sidebar  = useSidebar();
  const tab      = useTab();
  const user     = useUser();
  const whereBuy = useWhereBuy();

  const { current: tabs } = React.useRef([
    'label_authorization',
    'label_registration',
    'label_reset_password',
  ]);

  const [state, setState] = React.useState(null);

  const activeTab = tab[sidebarConstants.PROFILE_SIDEBAR] || 0;

  const email = React.useMemo(() => {
    return emailForm || user.email || null;
  }, [emailForm, user.email]);
  const show  = React.useMemo(() => {
    return sidebar.show && sidebar.sidebar === sidebarConstants.PROFILE_SIDEBAR;
  }, [sidebar.show, sidebar.sidebar]);
  const title = React.useMemo(() => {
    return user.authorized ? t('label_user_account') : state;
  }, [state, user.authorized]);

  const handleChangeTab          = React.useCallback((index) => {
    setState(tabs[index]);

    tab.changeTab(sidebarConstants.PROFILE_SIDEBAR, index);
  }, []);
  const submitAuthForm           = React.useCallback((event, handleSubmit) => {
    event.preventDefault();

    handleSubmit((values) => {
      return user.login(values, whereBuy.cities ?? [])
        .catch((error) => {
          throw new SubmissionError(error);
        });
    })(event);
  }, [whereBuy.cities]);
  const submitRegisterForm       = React.useCallback((event, handleSubmit) => {
    event.preventDefault();

    handleSubmit((values) => {
      return user.register(values)
        .then(() => {
          dispatch(reset('RegisterForm'));

          handleChangeTab(0);
        })
        .catch((error) => {
          throw new SubmissionError(error);
        });
    })(event);
  }, [dispatch, handleChangeTab]);
  const submitChangePasswordForm = React.useCallback((event, handleSubmit) => {
    event.preventDefault();

    handleSubmit((values) => {
      return user.changePassword(values)
        .catch((error) => {
          throw new SubmissionError(error);
        });
    })(event);
  }, []);

  const authFormProps           = React.useMemo(() => {
    let props = {
      email,
      isActive:   user.isActive,
      submitForm: submitAuthForm,
    };

    if (user.userInitialStateLoaded) {
      props.initialValues = { _token: user._token };
    }

    return props;
  }, [email, submitAuthForm, user._token, user.userInitialStateLoaded, user.isActive]);
  const changePasswordFormProps = React.useMemo(() => {
    let props = { submitForm: submitChangePasswordForm };

    if (user.userInitialStateLoaded) {
      props.initialValues = { _token: user._token };
    }

    return props;
  }, [submitChangePasswordForm, user._token, user.userInitialStateLoaded]);
  const registerFormProps       = React.useMemo(() => {
    let props = { submitForm: submitRegisterForm };

    if (user.userInitialStateLoaded) {
      props.initialValues = { _token: user._token };
    }

    return props;
  }, [submitRegisterForm, user._token, user.userInitialStateLoaded]);

  React.useEffect(() => {
    tab.registerTabsComponent(sidebarConstants.PROFILE_SIDEBAR);

    setState(tabs[activeTab]);
  }, []);

  if (!user.userInitialStateLoaded) {
    return null;
  }

  return (
    <BaseSidebar
      open={show}
      title={t(title)}
    >
      <div className={'body'}>
        {user.authorized
          ? (
            <div className="profile">
              <div className="list">
                <div>
                  <span>{t('field_firstName')}:</span>
                  <span>{t('field_lastName')}:</span>
                  <span>E-mail:</span>
                  <span>{t('field_phone')}:</span>
                  <span>{t('field_address')}:</span>
                  <span>{t('field_cityLabel')}:</span>
                </div>
                <div>
                  <span className={'label'}>{user.name.first_name || t('client_notFilled')}</span>
                  <span className={'label'}>{user.name.last_name || t('client_notFilled')}</span>
                  <span className={'label'}>{user.email || t('client_notFilled')}</span>
                  <span className={'label'}>{user.phone || t('client_notFilled')}</span>
                  <span className={'label'}>{user.address || t('client_notFilled')}</span>
                </div>
              </div>
              <div className="btns">
                <RenderButton
                  className={'fill'}
                  label={t('client_profile')}
                  onClick={sidebar.hideSidebar}
                  type={'link'}
                  uri={'/profile'}
                />
                <RenderButton
                  action={user.logout}
                  className={'fill'}
                  label={t('btn_output')}
                  type={'button'}
                />
              </div>
              {hasAccess.admin() && (
                <div className="fixed">
                  <a
                    className={'btn'}
                    href={'/admin-panel'}
                  >{t('dashboard_title')}</a>
                </div>
              )}
            </div>
          )
          : (
            <RenderBasicTabs
              active={activeTab}
              changeTab={handleChangeTab}
            >
              <AuthForm
                {...authFormProps}
                changeTab={handleChangeTab}
              />
              <RegisterForm
                {...registerFormProps}
                changeTab={handleChangeTab}
              />
              <ChangePasswordForm
                {...changePasswordFormProps}
                changeTab={handleChangeTab}
              />
            </RenderBasicTabs>
          )
        }
      </div>
    </BaseSidebar>
  );
};

function mapStateToProps(state) {
  const selector = formValueSelector('AuthForm');

  return {
    emailForm: selector(state, 'email'),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProfileSidebar);